import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Tag, CheckCircle, AlertCircle } from "lucide-react";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { API, useCart } from "../App";
import { toast } from "sonner";
import { PayPalButtons } from "@paypal/react-paypal-js";
import { motion } from "framer-motion";

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cart, fetchCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponApplied, setCouponApplied] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [orderId, setOrderId] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  
  const [address, setAddress] = useState({
    full_name: "",
    phone: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    pincode: "",
    country: "India"
  });

  const shippingFee = cart.total >= 500 ? 0 : 50;
  const discount = couponApplied?.calculated_discount || 0;
  const finalTotal = cart.total + shippingFee - discount;

  const handleAddressChange = (e) => {
    setAddress({ ...address, [e.target.name]: e.target.value });
  };

  const validateCoupon = async () => {
    if (!couponCode.trim()) return;
    
    try {
      const response = await axios.post(`${API}/coupons/validate`, {
        code: couponCode,
        subtotal: cart.total
      }, { withCredentials: true });
      
      setCouponApplied(response.data);
      setCouponError("");
      toast.success("Coupon applied successfully!");
    } catch (error) {
      setCouponError(error.response?.data?.detail || "Invalid coupon");
      setCouponApplied(null);
    }
  };

  const removeCoupon = () => {
    setCouponApplied(null);
    setCouponCode("");
    setCouponError("");
  };

  const isAddressValid = () => {
    return address.full_name && address.phone && address.address_line1 && 
           address.city && address.state && address.pincode;
  };

  const createOrder = async () => {
    if (!isAddressValid()) {
      toast.error("Please fill in all required address fields");
      return null;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/orders/create`, {
        shipping_address: address,
        payment_method: "paypal",
        coupon_code: couponApplied?.code || null
      }, { withCredentials: true });
      
      setOrderId(response.data.order_id);
      setOrderDetails(response.data);
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create order");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = async (paymentId) => {
    if (!orderId) return;
    
    try {
      await axios.post(`${API}/orders/${orderId}/pay`, {
        payment_id: paymentId
      }, { withCredentials: true });
      
      toast.success("Payment successful!");
      await fetchCart();
      navigate(`/orders/${orderId}`);
    } catch (error) {
      toast.error("Payment verification failed");
    }
  };

  // For demo purposes - simulate PayPal
  const handleDemoPayment = async () => {
    const order = await createOrder();
    if (order) {
      await handlePaymentSuccess(`PAYPAL_DEMO_${Date.now()}`);
    }
  };

  if (cart.items.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="checkout-page">
      <Header />
      
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button 
            onClick={() => navigate('/cart')}
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Cart
          </button>

          <h1 className="text-3xl font-bold text-foreground mb-8">Checkout</h1>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Shipping Address */}
            <div className="lg:col-span-2 space-y-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-card border border-white/5 rounded-xl p-6"
                data-testid="shipping-address-form"
              >
                <h2 className="text-lg font-semibold text-foreground mb-6">Shipping Address</h2>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="full_name">Full Name *</Label>
                    <Input
                      id="full_name"
                      name="full_name"
                      value={address.full_name}
                      onChange={handleAddressChange}
                      placeholder="John Doe"
                      className="bg-background border-white/10"
                      data-testid="input-full-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={address.phone}
                      onChange={handleAddressChange}
                      placeholder="+91 98765 43210"
                      className="bg-background border-white/10"
                      data-testid="input-phone"
                    />
                  </div>
                  <div className="sm:col-span-2 space-y-2">
                    <Label htmlFor="address_line1">Address Line 1 *</Label>
                    <Input
                      id="address_line1"
                      name="address_line1"
                      value={address.address_line1}
                      onChange={handleAddressChange}
                      placeholder="House/Flat No., Building Name"
                      className="bg-background border-white/10"
                      data-testid="input-address1"
                    />
                  </div>
                  <div className="sm:col-span-2 space-y-2">
                    <Label htmlFor="address_line2">Address Line 2</Label>
                    <Input
                      id="address_line2"
                      name="address_line2"
                      value={address.address_line2}
                      onChange={handleAddressChange}
                      placeholder="Street, Landmark (Optional)"
                      className="bg-background border-white/10"
                      data-testid="input-address2"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      name="city"
                      value={address.city}
                      onChange={handleAddressChange}
                      placeholder="Mumbai"
                      className="bg-background border-white/10"
                      data-testid="input-city"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">State *</Label>
                    <Input
                      id="state"
                      name="state"
                      value={address.state}
                      onChange={handleAddressChange}
                      placeholder="Maharashtra"
                      className="bg-background border-white/10"
                      data-testid="input-state"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pincode">Pincode *</Label>
                    <Input
                      id="pincode"
                      name="pincode"
                      value={address.pincode}
                      onChange={handleAddressChange}
                      placeholder="400001"
                      className="bg-background border-white/10"
                      data-testid="input-pincode"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="country">Country</Label>
                    <Input
                      id="country"
                      name="country"
                      value={address.country}
                      disabled
                      className="bg-background border-white/10 opacity-50"
                    />
                  </div>
                </div>
              </motion.div>

              {/* Coupon Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-card border border-white/5 rounded-xl p-6"
                data-testid="coupon-section"
              >
                <h2 className="text-lg font-semibold text-foreground mb-4">Apply Coupon</h2>
                
                {couponApplied ? (
                  <div className="flex items-center justify-between p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <div>
                        <p className="font-medium text-foreground">{couponApplied.code}</p>
                        <p className="text-sm text-green-500">
                          {couponApplied.discount_type === 'percentage' 
                            ? `${couponApplied.discount_value}% off` 
                            : `₹${couponApplied.discount_value} off`}
                        </p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={removeCoupon} className="text-red-500">
                      Remove
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-3">
                    <div className="flex-1 relative">
                      <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        placeholder="Enter coupon code"
                        className="pl-10 bg-background border-white/10"
                        data-testid="coupon-input"
                      />
                    </div>
                    <Button 
                      onClick={validateCoupon}
                      variant="outline"
                      className="border-white/20"
                      data-testid="apply-coupon-btn"
                    >
                      Apply
                    </Button>
                  </div>
                )}
                
                {couponError && (
                  <div className="flex items-center gap-2 mt-3 text-red-500 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    {couponError}
                  </div>
                )}
                
                <p className="text-xs text-muted-foreground mt-3">
                  Try <span className="text-secondary font-mono">WELCOME10</span> for 10% off (min. ₹200)
                </p>
              </motion.div>

              {/* Payment Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-card border border-white/5 rounded-xl p-6"
                data-testid="payment-section"
              >
                <h2 className="text-lg font-semibold text-foreground mb-4">Payment</h2>
                
                <div className="paypal-buttons-container">
                  {/* PayPal Demo Button */}
                  <Button
                    onClick={handleDemoPayment}
                    disabled={!isAddressValid() || loading}
                    className="w-full bg-[#0070ba] hover:bg-[#003087] text-white py-6 rounded-lg text-base font-medium"
                    data-testid="paypal-demo-btn"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <div className="spinner w-5 h-5 border-2"></div>
                        Processing...
                      </span>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.641.641 0 0 1 .633-.54h6.604c2.18 0 3.878.534 4.903 1.543.963.947 1.348 2.312 1.143 4.055-.28 2.383-1.168 4.236-2.636 5.502-1.503 1.297-3.61 1.955-6.262 1.955H7.076v5.102zm10.074-12.5c.024-.214.036-.416.036-.606 0-1.063-.363-1.867-1.081-2.39-.718-.523-1.85-.79-3.364-.79H8.348l-1.28 8.21h2.463c1.893 0 3.393-.472 4.46-1.402 1.066-.93 1.76-2.281 2.06-4.023l.099-.999z"/>
                        </svg>
                        Pay with PayPal - ₹{finalTotal}
                      </>
                    )}
                  </Button>
                  
                  <p className="text-xs text-center text-muted-foreground mt-4">
                    Demo mode: Click to simulate PayPal payment
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-card border border-white/5 rounded-xl p-6 sticky top-24" data-testid="checkout-summary">
                <h2 className="text-lg font-semibold text-foreground mb-6">Order Summary</h2>
                
                {/* Items */}
                <div className="space-y-4 mb-6">
                  {cart.items.map((item) => (
                    <div key={item.product_id} className="flex gap-3">
                      <img src={item.image_url} alt={item.name} className="w-16 h-16 rounded-lg object-cover" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-foreground line-clamp-1">{item.name}</p>
                        <p className="text-xs text-muted-foreground">Qty: {item.quantity}</p>
                        <p className="text-sm font-medium text-foreground">₹{item.subtotal}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-white/5 pt-4 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="text-foreground">₹{cart.total}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Shipping</span>
                    {shippingFee === 0 ? (
                      <span className="text-green-500">FREE</span>
                    ) : (
                      <span className="text-foreground">₹{shippingFee}</span>
                    )}
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Discount</span>
                      <span className="text-green-500">-₹{discount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="border-t border-white/5 pt-3">
                    <div className="flex justify-between">
                      <span className="font-semibold text-foreground">Total</span>
                      <span className="text-xl font-bold text-foreground" data-testid="checkout-total">₹{finalTotal.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CheckoutPage;
